import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts'
import {
  FaArrowDown,
  FaArrowRight,
  FaArrowUp,
  FaCheckCircle,
  FaChevronRight,
  FaFileInvoiceDollar,
  FaInbox,
  FaFileSignature,
  FaHardHat,
  FaSlidersH,
  FaSyncAlt,
  FaTrophy,
} from 'react-icons/fa'

import {
  getChartData,
  getEnquiriesCreatedThisMonth,
  getEnquiriesCreatedToday,
  getEnquiriesNotQuoted,
  getInvoicesCreatedToday,
  getJobsCompletedToday,
  getJobsCreatedThisMonth,
  getJobsCreatedToday,
  getQuotesCreatedThisMonth,
  getQuotesCreatedToday,
  getQuotesRecent,
  getQuotesWonPercent,
  getQuotesWonPercentLastYear,
  getQuotesWonToday,
} from '../../data/dashboardData'
import { getCurrentUserEmployee } from '../../data/employeeData'
import { logoWhiteSVG } from '../../assets'
import WeatherWidget from '../../components/WeatherWidget'
import TaskListPreview from './TaskListPreview'
import WidgetPicker, { DEFAULT_WIDGETS } from './WidgetPicker'
import './HomePreview.css'

/**
 * A redesigned home page, built alongside the live one (pages/HomePage) so the
 * new look can be worked on without changing what everyone sees. It reads
 * exactly the same figures from the same endpoints; only the presentation is
 * new. Everything it styles is scoped under .hp, so none of it leaks into the
 * rest of the portal.
 *
 * Reached at /home-preview, and deliberately not linked from the menu.
 */

/** One month of the rolling twelve, oldest first. */
interface MonthPoint {
  month: string
  jobs: number
  quotes: number
  invoices?: number
  jobsPreviousYear: number
  quotesPreviousYear: number
}

interface Figures {
  jobsToday: number
  jobsCompletedToday: number
  jobsMonth: number
  quotesToday: number
  quotesWonToday: number
  quotesMonth: number
  quotesOpen: number
  quotesWonMonthPct: number
  quotesWonYtdPct: number
  enquiriesToday: number
  enquiriesMonth: number
  enquiriesOpen: number
  invoicesToday: number
}

const REFRESH_MS = 60_000

/**
 * How tightly the page is laid out. Compact fits the most on screen;
 * Comfortable is the default. Spacing comes from CSS tokens per density - the
 * chart and rings, sized in code, take theirs from here.
 */
type Density = 'compact' | 'comfortable' | 'spacious'

const DENSITIES: { key: Density; label: string }[] = [
  { key: 'compact', label: 'Compact' },
  { key: 'comfortable', label: 'Comfortable' },
  { key: 'spacious', label: 'Spacious' },
]

const DENSITY_SIZES: Record<Density, { chart: number; ring: number; ringSmall: number }> = {
  compact: { chart: 190, ring: 104, ringSmall: 88 },
  comfortable: { chart: 230, ring: 120, ringSmall: 100 },
  spacious: { chart: 280, ring: 132, ringSmall: 108 },
}

const DENSITY_KEY = 'homePreviewDensity'

// A per-browser convenience: the page works the same if it can't be read.
const readDensity = (): Density => {
  try {
    const saved = window.localStorage.getItem(DENSITY_KEY)
    return saved === 'compact' || saved === 'spacious' ? saved : 'comfortable'
  } catch {
    return 'comfortable'
  }
}

const writeDensity = (density: Density) => {
  try {
    window.localStorage.setItem(DENSITY_KEY, density)
  } catch {
    // Not remembering is fine.
  }
}

const greeting = (d: Date) => {
  const h = d.getHours()
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'
}

const longDate = (d: Date) =>
  d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

const clockTime = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })

const pct = (v: number) => `${Math.round(v)}%`

/** Change from b to a as a whole percentage; null when there's nothing to compare against. */
const change = (a: number, b: number) => (b > 0 ? Math.round(((a - b) / b) * 100) : null)

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/** Counts up to the value the first time it arrives, then follows it. */
const useCountUp = (target: number | null, duration = 700) => {
  const [shown, setShown] = useState(0)
  const from = useRef(0)

  useEffect(() => {
    if (target === null) return
    if (prefersReducedMotion()) {
      setShown(target)
      from.current = target
      return
    }

    const start = performance.now()
    const begin = from.current
    let frame = 0

    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1)
      const eased = 1 - Math.pow(1 - t, 3)
      setShown(begin + (target - begin) * eased)
      if (t < 1) frame = requestAnimationFrame(tick)
      else from.current = target
    }

    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [target, duration])

  return shown
}

const HomePreview = () => {
  const navigate = useNavigate()

  const [figures, setFigures] = useState<Figures | null>(null)
  const [months, setMonths] = useState<MonthPoint[] | null>(null)
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [now, setNow] = useState(new Date())
  const [firstName, setFirstName] = useState('')

  // Just for the greeting - it reads fine without a name if this fails.
  useEffect(() => {
    let cancelled = false
    getCurrentUserEmployee()
      .then((employee) => {
        const first = employee?.employeeName?.trim().split(/\s+/)[0]
        if (!cancelled && first) setFirstName(first)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const load = useCallback(async () => {
    setRefreshing(true)

    // Settled rather than all-or-nothing: one slow or failing figure shouldn't
    // blank the whole page. Anything that fails keeps its last value.
    const value = <T,>(r: PromiseSettledResult<T>, fallback: T) =>
      r.status === 'fulfilled' && r.value != null ? r.value : fallback

    const results = await Promise.allSettled([
      getJobsCreatedToday(),
      getJobsCompletedToday(),
      getJobsCreatedThisMonth(),
      getQuotesCreatedToday(),
      getQuotesWonToday(),
      getQuotesCreatedThisMonth(),
      getQuotesRecent(),
      getQuotesWonPercent(),
      getQuotesWonPercentLastYear(),
      getEnquiriesCreatedToday(),
      getEnquiriesCreatedThisMonth(),
      getEnquiriesNotQuoted(),
      getInvoicesCreatedToday(),
      getChartData(),
    ])

    setFigures((prev) => {
      const p: Figures = prev ?? {
        jobsToday: 0,
        jobsCompletedToday: 0,
        jobsMonth: 0,
        quotesToday: 0,
        quotesWonToday: 0,
        quotesMonth: 0,
        quotesOpen: 0,
        quotesWonMonthPct: 0,
        quotesWonYtdPct: 0,
        enquiriesToday: 0,
        enquiriesMonth: 0,
        enquiriesOpen: 0,
        invoicesToday: 0,
      }
      return {
        jobsToday: Number(value(results[0], p.jobsToday)),
        jobsCompletedToday: Number(value(results[1], p.jobsCompletedToday)),
        jobsMonth: Number(value(results[2], p.jobsMonth)),
        quotesToday: Number(value(results[3], p.quotesToday)),
        quotesWonToday: Number(value(results[4], p.quotesWonToday)),
        quotesMonth: Number(value(results[5], p.quotesMonth)),
        quotesOpen: Number(value(results[6], p.quotesOpen)),
        quotesWonMonthPct: Number(value(results[7], p.quotesWonMonthPct)),
        quotesWonYtdPct: Number(value(results[8], p.quotesWonYtdPct)),
        enquiriesToday: Number(value(results[9], p.enquiriesToday)),
        enquiriesMonth: Number(value(results[10], p.enquiriesMonth)),
        enquiriesOpen: Number(value(results[11], p.enquiriesOpen)),
        invoicesToday: Number(value(results[12], p.invoicesToday)),
      }
    })

    const chart = results[13]
    if (chart.status === 'fulfilled' && Array.isArray(chart.value)) {
      setMonths(chart.value as MonthPoint[])
    }

    setUpdatedAt(new Date())
    setRefreshing(false)
  }, [])

  useEffect(() => {
    load()
    const refresh = setInterval(load, REFRESH_MS)
    const clock = setInterval(() => setNow(new Date()), 30_000)
    return () => {
      clearInterval(refresh)
      clearInterval(clock)
    }
  }, [load])

  const f = figures

  // Which widgets are on. Preview only: held for this visit, not saved.
  const [enabled, setEnabled] = useState<string[]>(DEFAULT_WIDGETS)
  const [pickerOpen, setPickerOpen] = useState(false)
  const show = (id: string) => enabled.includes(id)

  const [density, setDensity] = useState<Density>(readDensity)
  const sizes = DENSITY_SIZES[density]
  const chooseDensity = (next: Density) => {
    setDensity(next)
    writeDensity(next)
  }

  return (
    <div
      className={`hp${density === 'comfortable' ? '' : ` hp--${density}`}`}
      style={{ '--hp-chart-height': `${sizes.chart}px` } as React.CSSProperties}
    >
      {/* --- Hero: who, when, and today at a glance -------------------------- */}
      <header className='hp-hero'>
        <div className='hp-hero__glow' aria-hidden='true' />

        <div className='hp-hero__top'>
          <div className='hp-hero__brand'>
            <img src={logoWhiteSVG} alt='Bamfords' className='hp-hero__logo' />
            <span className='hp-hero__divider' aria-hidden='true' />
            <div>
              <h1 className='hp-hero__greeting'>
                {greeting(now)}
                {firstName && `, ${firstName}`}
              </h1>
              <p className='hp-hero__date'>{longDate(now)}</p>
            </div>
          </div>

          <div className='hp-hero__aside'>
            <div className='hp-hero__weather'>
              <WeatherWidget />
            </div>
            <button
              type='button'
              className='hp-live'
              onClick={load}
              disabled={refreshing}
              title='Refresh now'
            >
              <span className={`hp-live__dot${refreshing ? ' hp-live__dot--busy' : ''}`} aria-hidden='true' />
              {updatedAt ? `Live · ${clockTime(updatedAt)}` : 'Loading…'}
              <FaSyncAlt className={`hp-live__icon${refreshing ? ' hp-spin' : ''}`} aria-hidden='true' />
            </button>
          </div>
        </div>

        {show('today') && (
        <div className='hp-today'>
          <p className='hp-eyebrow hp-eyebrow--light'>Today</p>
          <div className='hp-today__grid'>
            <TodayTile
              icon={<FaCheckCircle />}
              label='Jobs completed'
              value={f?.jobsCompletedToday ?? null}
              onClick={() => navigate('/jobs')}
            />
            <TodayTile
              icon={<FaTrophy />}
              label='Quotes won'
              value={f?.quotesWonToday ?? null}
              onClick={() => navigate('/quotes')}
            />
            <TodayTile
              icon={<FaFileInvoiceDollar />}
              label='Invoices raised'
              value={f?.invoicesToday ?? null}
              onClick={() => navigate('/invoicing')}
            />
          </div>
        </div>
        )}
      </header>

      {/* --- Your dashboard: and the way to choose what's on it ---------------- */}
      <div className='hp-toolbar'>
        <div>
          <h2 className='hp-section__title'>Your dashboard</h2>
          <p className='hp-muted hp-toolbar__sub' style={{ margin: '2px 0 0' }}>
            Choose what you see here - everyone's home page can be different.
          </p>
        </div>
        <div className='hp-toolbar__actions'>
          <div className='hp-density'>
            <span className='hp-density__label' id='hp-density-label'>
              Density
            </span>
            <div className='hp-segment' role='radiogroup' aria-labelledby='hp-density-label'>
              {DENSITIES.map((d) => (
                <button
                  key={d.key}
                  type='button'
                  role='radio'
                  aria-checked={density === d.key}
                  className={`hp-segment__btn${density === d.key ? ' hp-segment__btn--on' : ''}`}
                  onClick={() => chooseDensity(d.key)}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <button type='button' className='hp-btn hp-btn--ghost' onClick={() => setPickerOpen(true)}>
            <FaSlidersH aria-hidden='true' /> Customise
          </button>
        </div>
      </div>

      {/* --- Pipeline: enquiry to quote to job ------------------------------- */}
      {show('pipeline') && (
      <section className='hp-section'>
        <div className='hp-section__head'>
          <div>
            <p className='hp-eyebrow'>Pipeline</p>
            <h2 className='hp-section__title'>From enquiry to job</h2>
          </div>
        </div>

        <div className='hp-flow'>
          <StageCard
            tone='violet'
            icon={<FaInbox />}
            name='Enquiries'
            headline={f?.enquiriesOpen ?? null}
            headlineLabel='open, not yet quoted'
            today={f?.enquiriesToday ?? null}
            month={f?.enquiriesMonth ?? null}
            onOpen={() => navigate('/enquiries')}
          />
          <FlowArrow />
          <StageCard
            tone='teal'
            icon={<FaFileSignature />}
            name='Quotes'
            headline={f?.quotesOpen ?? null}
            headlineLabel='open'
            today={f?.quotesToday ?? null}
            month={f?.quotesMonth ?? null}
            onOpen={() => navigate('/quotes')}
          />
          <FlowArrow />
          <StageCard
            tone='lime'
            icon={<FaHardHat />}
            name='Jobs'
            headline={f?.jobsMonth ?? null}
            headlineLabel='created this month'
            today={f?.jobsToday ?? null}
            month={null}
            showMonth={false}
            onOpen={() => navigate('/jobs')}
          />
        </div>
      </section>

      )}

      {/* --- Tasks and win rate ----------------------------------------------- */}
      {(show('tasks') || show('winrate')) && (
        <section className={`hp-section${show('tasks') && show('winrate') ? ' hp-split hp-split--tasks' : ''}`}>
          {show('tasks') && <TaskListPreview />}
          {show('winrate') && (
            <WinRateCard
              month={f?.quotesWonMonthPct ?? null}
              ytd={f?.quotesWonYtdPct ?? null}
              ring={sizes.ring}
              ringSmall={sizes.ringSmall}
            />
          )}
        </section>
      )}

      {/* --- The twelve month trend ------------------------------------------- */}
      {show('trend') && (
        <section className='hp-section'>
          <TrendCard months={months} height={sizes.chart} />
        </section>
      )}

      {/* Ideas-only widgets don't draw anything yet, so it's the real ones that count. */}
      {!DEFAULT_WIDGETS.some(show) && (
        <div className='hp-card hp-blank'>
          <p className='hp-section__title'>Your home page is empty</p>
          <p className='hp-muted'>Choose what you'd like to see here.</p>
          <button type='button' className='hp-btn hp-btn--primary' onClick={() => setPickerOpen(true)}>
            <FaSlidersH aria-hidden='true' /> Customise
          </button>
        </div>
      )}

      {pickerOpen && (
        <WidgetPicker
          enabled={enabled}
          onToggle={(id) =>
            setEnabled((on) => (on.includes(id) ? on.filter((x) => x !== id) : [...on, id]))
          }
          onReset={() => setEnabled(DEFAULT_WIDGETS)}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  )
}

// --- Pieces ----------------------------------------------------------------------

const Count = ({ value, percent = false }: { value: number | null; percent?: boolean }) => {
  const shown = useCountUp(value)
  if (value === null) return <span className='hp-skeleton hp-skeleton--num' aria-label='Loading' />
  return <>{percent ? pct(shown) : Math.round(shown).toLocaleString('en-GB')}</>
}

const TodayTile = ({
  icon,
  label,
  value,
  onClick,
}: {
  icon: JSX.Element
  label: string
  value: number | null
  onClick: () => void
}) => (
  <button type='button' className='hp-tile' onClick={onClick}>
    <span className='hp-tile__icon' aria-hidden='true'>
      {icon}
    </span>
    <span className='hp-tile__body'>
      <span className='hp-tile__value'>
        <Count value={value} />
      </span>
      <span className='hp-tile__label'>{label}</span>
    </span>
    <FaChevronRight className='hp-tile__go' aria-hidden='true' />
  </button>
)

const StageCard = ({
  tone,
  icon,
  name,
  headline,
  headlineLabel,
  today,
  month,
  showMonth = true,
  onOpen,
}: {
  tone: 'violet' | 'teal' | 'lime'
  icon: JSX.Element
  name: string
  headline: number | null
  headlineLabel: string
  today: number | null
  month: number | null
  /** Off when the headline already is this month's figure. */
  showMonth?: boolean
  onOpen: () => void
}) => (
  <article className={`hp-card hp-stage hp-stage--${tone}`}>
    <div className='hp-stage__head'>
      <span className='hp-stage__icon' aria-hidden='true'>
        {icon}
      </span>
      <h3 className='hp-stage__name'>{name}</h3>
      <button type='button' className='hp-link' onClick={onOpen}>
        View <FaArrowRight aria-hidden='true' />
      </button>
    </div>

    <div className='hp-stage__headline'>
      <span className='hp-stage__value'>
        <Count value={headline} />
      </span>
      <span className='hp-stage__caption'>{headlineLabel}</span>
    </div>

    <dl className='hp-stage__metrics'>
      <div>
        <dt>Today</dt>
        <dd>
          <Count value={today} />
        </dd>
      </div>
      {showMonth && (
        <div>
          <dt>This month</dt>
          <dd>
            <Count value={month} />
          </dd>
        </div>
      )}
    </dl>
  </article>
)

const FlowArrow = () => (
  <div className='hp-flow__arrow' aria-hidden='true'>
    <FaChevronRight />
  </div>
)

/** A ring filled to a percentage. */
const Ring = ({ value, size = 132, stroke = 12, tone }: { value: number | null; size?: number; stroke?: number; tone: string }) => {
  const shown = useCountUp(value)
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const filled = Math.max(0, Math.min(100, shown)) / 100

  return (
    <div className='hp-ring' style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden='true'>
        <circle cx={size / 2} cy={size / 2} r={r} className='hp-ring__track' strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          className={`hp-ring__fill hp-ring__fill--${tone}`}
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={c * (1 - filled)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <span className='hp-ring__value'>
        {value === null ? <span className='hp-skeleton hp-skeleton--num' /> : pct(shown)}
      </span>
    </div>
  )
}

const Delta = ({ value, suffix }: { value: number | null; suffix: string }) => {
  if (value === null) return null
  const up = value > 0
  const flat = value === 0
  return (
    <span className={`hp-delta ${flat ? 'hp-delta--flat' : up ? 'hp-delta--up' : 'hp-delta--down'}`}>
      {flat ? null : up ? <FaArrowUp aria-hidden='true' /> : <FaArrowDown aria-hidden='true' />}
      {flat ? 'No change' : `${Math.abs(value)}${suffix}`}
    </span>
  )
}

const WinRateCard = ({
  month,
  ytd,
  ring,
  ringSmall,
}: {
  month: number | null
  ytd: number | null
  ring: number
  ringSmall: number
}) => {
  // Percentage points, not a percentage of a percentage.
  const diff = month !== null && ytd !== null ? Math.round(month - ytd) : null

  return (
    <article className='hp-card hp-win'>
      <p className='hp-eyebrow'>Quote win rate</p>
      <div className='hp-win__rings'>
        <div className='hp-win__ring'>
          <Ring value={month} tone='brand' size={ring} stroke={Math.round(ring / 11)} />
          <span className='hp-win__label'>This month</span>
        </div>
        <div className='hp-win__ring'>
          <Ring value={ytd} tone='navy' size={ringSmall} stroke={Math.round(ringSmall / 11)} />
          <span className='hp-win__label'>Year to date</span>
        </div>
      </div>
      <div className='hp-win__foot'>
        <Delta value={diff} suffix=' pts' />
        <span className='hp-muted'>this month against the year so far</span>
      </div>
    </article>
  )
}

type Series = 'quotes' | 'jobs'

const TrendTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (!active || !payload?.length) return null
  return (
    <div className='hp-tooltip'>
      <p className='hp-tooltip__title'>{label}</p>
      {payload.map((p) => (
        <p key={String(p.dataKey)} className='hp-tooltip__row'>
          <span className='hp-tooltip__swatch' style={{ background: p.color }} />
          {p.name}
          <strong>{Number(p.value).toLocaleString('en-GB')}</strong>
        </p>
      ))}
    </div>
  )
}

const TrendCard = ({ months, height }: { months: MonthPoint[] | null; height: number }) => {
  const [series, setSeries] = useState<Series>('quotes')

  const prevKey = series === 'quotes' ? 'quotesPreviousYear' : 'jobsPreviousYear'

  const totals = useMemo(() => {
    if (!months) return null
    const current = months.reduce((s, m) => s + (m[series] || 0), 0)
    const previous = months.reduce((s, m) => s + (m[prevKey] || 0), 0)
    return { current, previous, change: change(current, previous) }
  }, [months, series, prevKey])

  const label = series === 'quotes' ? 'Quotes' : 'Jobs'

  return (
    <article className='hp-card hp-trend'>
      <div className='hp-trend__head'>
        <div>
          <p className='hp-eyebrow'>Last 12 months</p>
          <div className='hp-trend__total'>
            <span className='hp-trend__value'>
              {totals ? totals.current.toLocaleString('en-GB') : <span className='hp-skeleton hp-skeleton--num' />}
            </span>
            <span className='hp-trend__unit'>{label.toLowerCase()} created</span>
            {totals && <Delta value={totals.change} suffix='%' />}
          </div>
          <p className='hp-muted hp-trend__sub'>
            {totals ? `${totals.previous.toLocaleString('en-GB')} in the 12 months before` : ' '}
          </p>
        </div>

        <div className='hp-segment' role='tablist' aria-label='Show'>
          {(['quotes', 'jobs'] as Series[]).map((s) => (
            <button
              key={s}
              type='button'
              role='tab'
              aria-selected={series === s}
              className={`hp-segment__btn${series === s ? ' hp-segment__btn--on' : ''}`}
              onClick={() => setSeries(s)}
            >
              {s === 'quotes' ? 'Quotes' : 'Jobs'}
            </button>
          ))}
        </div>
      </div>

      <div className='hp-trend__chart'>
        {months ? (
          <ResponsiveContainer width='100%' height={height}>
            <BarChart data={months} barGap={4} barCategoryGap='24%' margin={{ top: 8, right: 4, left: -18, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke='#eef1f6' />
              <XAxis
                dataKey='month'
                tickFormatter={(v: string) => v.split(' ')[0]}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12, fill: '#7a8499' }}
                tickMargin={10}
              />
              <YAxis
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12, fill: '#7a8499' }}
                width={44}
              />
              <Tooltip content={<TrendTooltip />} cursor={{ fill: 'rgba(30, 42, 78, 0.05)', radius: 6 }} />
              <Bar dataKey={prevKey} name='Previous year' fill='#d6dcea' radius={[6, 6, 0, 0]} maxBarSize={18} />
              <Bar
                dataKey={series}
                name='This year'
                fill={series === 'quotes' ? '#1e2a4e' : '#e9530d'}
                radius={[6, 6, 0, 0]}
                maxBarSize={18}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className='hp-skeleton hp-skeleton--chart' />
        )}
      </div>

      <div className='hp-legend'>
        <span>
          <i className='hp-legend__swatch' style={{ background: series === 'quotes' ? '#1e2a4e' : '#e9530d' }} />
          This year
        </span>
        <span>
          <i className='hp-legend__swatch' style={{ background: '#d6dcea' }} />
          Same month last year
        </span>
      </div>
    </article>
  )
}

export default HomePreview
